import {
  Box,
  Button,
  Fieldset,
  Flex,
  Input,
  Link as ChakraLink,
  Stack,
  Text
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { Link as ReactRouterLink, useNavigate } from 'react-router-dom';

import { fetchAPI } from '@/api/fetch';
import { apiSendVerificationCode } from '@/api/services';
import { CaretLeft, Eye, EyeSlash } from '@phosphor-icons/react';
import { Checkbox } from '@/components/ui/checkbox';
import { SignUpStep, useSignUp } from '@/store/signup';

const emailExistError = 'Email already exists. Please use a different email.';

type FormValues = {
  email: string;
  password: string;
  confirmPassword: string;
  name: string;
  agencyName: string;
  agreeToTerms: boolean;
};

const calculatePasswordStrength = (password: string): number => {
  let strength = 0;
  if (password.length >= 8) strength += 25;
  if (/[A-Z]/.test(password)) strength += 25;
  if (/[a-z]/.test(password)) strength += 25;
  if (/[0-9]/.test(password)) strength += 25;
  return strength;
};

const getPasswordStrengthColor = (strength: number): string => {
  if (strength <= 25) return 'error.700';
  if (strength <= 50) return 'warning.700';
  if (strength <= 75) return 'info.700';
  return 'success.700';
};

export default function CreateAccount() {
  const signUpStore = useSignUp();
  const navigate = useNavigate();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const {
    handleSubmit,
    register,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    shouldFocusError: false,
    defaultValues: {
      email: signUpStore.email,
      name: signUpStore.name,
      agencyName: signUpStore.agencyName
    }
  });

  const password = watch('password');
  const agreeToTerms = watch('agreeToTerms');

  const onSubmit = async (values: FormValues) => {
    setSubmitError(null);

    try {
      const response = await fetchAPI({
        request: () =>
          apiSendVerificationCode({
            email: values.email,
            name: values.name
          })
      });

      if (response.success) {
        signUpStore.setStep(SignUpStep.EmailVerification);
        signUpStore.setEmail(values.email);
        signUpStore.setPassword(values.password);
        signUpStore.setName(values.name);
        signUpStore.setAgencyName(values.agencyName);
        signUpStore.setAgreeToTerms(values.agreeToTerms);
      } else {
        console.error(response.message);
        if (response.message === 'Email exists') {
          setSubmitError(emailExistError);
        } else {
          setSubmitError(response.message ?? 'Something went wrong. Please try again.');
        }
      }
    } catch (error) {
      setSubmitError('Something went wrong. Please try again.');
      console.error(error);
    }
  };

  const handleBack = () => {
    if (!isSubmitting) {
      signUpStore.reset();
      navigate('/login');
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPasswordStrength(calculatePasswordStrength(newPassword));
  };

  return (
    <Stack w="100%" h="100%">
      <Flex justifyContent="left" p="12px 50px" alignItems="center" gap="16px">
        <CaretLeft
          weight="bold"
          size={28}
          color="neutrals.900"
          onClick={handleBack}
          cursor={isSubmitting ? 'not-allowed' : 'pointer'}
        />
        <Text fontSize="32px" fontWeight={700}>
          Create Account
        </Text>
      </Flex>
      <Stack overflowY="auto">
        <form onSubmit={handleSubmit(onSubmit)}>
          <Fieldset.Root
            borderRadius="10px"
            w="100%"
            h="100%"
            p="0 120px 48px 120px"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
          >
            <Box>
              <Box>
                <Text fontWeight={500} fontSize="16px" color="neutrals.900" mb="10px">
                  Full Name
                </Text>
                <Input
                  fontSize="16px"
                  id="name"
                  placeholder="Enter your full name"
                  _placeholder={{
                    color: 'neutrals.300'
                  }}
                  borderColor={errors.name ? 'error.700' : 'neutrals.300'}
                  variant="outline"
                  p="12px 16px"
                  h="48px"
                  borderRadius="8px"
                  {...register('name', {
                    required: 'Full name is required.'
                  })}
                />
                {errors.name && (
                  <Text fontSize="14px" color="error.700" mt="5px" ml="5px">
                    {errors.name.message}
                  </Text>
                )}
              </Box>
              <Box mt="20px">
                <Text fontWeight={500} fontSize="16px" color="neutrals.900" mb="10px">
                  Agency Name
                </Text>
                <Input
                  fontSize="16px"
                  id="agency_name"
                  placeholder="Enter your agency name"
                  _placeholder={{
                    color: 'neutrals.300'
                  }}
                  borderColor={errors.agencyName ? 'error.700' : 'neutrals.300'}
                  variant="outline"
                  p="12px 16px"
                  h="48px"
                  borderRadius="8px"
                  {...register('agencyName', {
                    required: 'Agency name is required.'
                  })}
                />
                {errors.agencyName && (
                  <Text fontSize="14px" color="error.700" mt="5px" ml="5px">
                    {errors.agencyName.message}
                  </Text>
                )}
              </Box>
              <Box mt="20px">
                <Text fontWeight={500} fontSize="16px" color="neutrals.900" mb="10px">
                  Email
                </Text>
                <Input
                  fontSize="16px"
                  id="email"
                  placeholder="Enter your email"
                  _placeholder={{
                    color: 'neutrals.300'
                  }}
                  borderColor={
                    errors.email || submitError === emailExistError ? 'error.700' : 'neutrals.300'
                  }
                  variant="outline"
                  p="12px 16px"
                  h="48px"
                  autoComplete="email"
                  borderRadius="8px"
                  {...register('email', {
                    required: 'Email is required.',
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: 'Please enter a valid email address.'
                    }
                  })}
                />
                {errors.email && (
                  <Text fontSize="14px" color="error.700" mt="5px" ml="5px">
                    {errors.email.message}
                  </Text>
                )}
              </Box>
              <Box mt="20px">
                <Text fontWeight={500} fontSize="16px" color="neutrals.900" mb="10px">
                  Password
                </Text>
                <Flex alignItems="center" position="relative">
                  <Input
                    style={{
                      fontSize: '16px',
                      fontWeight: 400
                    }}
                    id="password"
                    placeholder="Create a password"
                    _placeholder={{
                      color: 'neutrals.300'
                    }}
                    borderColor={errors.password ? 'error.700' : 'neutrals.300'}
                    variant="outline"
                    type={showPassword ? 'text' : 'password'}
                    p="12px 16px"
                    h="48px"
                    autoComplete="new-password"
                    borderRadius="8px"
                    {...register('password', {
                      required: 'Password is required.',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters.'
                      },
                      maxLength: {
                        value: 30,
                        message: 'Password must be at most 30 characters.'
                      },
                      pattern: {
                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
                        message:
                          'Password must contain at least one uppercase letter, one lowercase letter, and one number.'
                      },
                      onChange: handlePasswordChange
                    })}
                  />
                  <Box
                    onClick={() => setShowPassword(!showPassword)}
                    position="absolute"
                    right="10px"
                    cursor="pointer"
                  >
                    {showPassword ? <EyeSlash size={24} /> : <Eye size={24} />}
                  </Box>
                </Flex>
                {password && (
                  <Box mt="8px">
                    <Box w="100%" h="4px" bg="neutrals.100" borderRadius="4px" overflow="hidden">
                      <Box
                        h="100%"
                        w={`${passwordStrength}%`}
                        bg={getPasswordStrengthColor(passwordStrength)}
                        transition="all 0.3s ease"
                      />
                    </Box>
                    <Text
                      fontSize="12px"
                      color={getPasswordStrengthColor(passwordStrength)}
                      mt="4px"
                    >
                      {passwordStrength <= 25
                        ? 'Weak'
                        : passwordStrength <= 50
                        ? 'Fair'
                        : passwordStrength <= 75
                        ? 'Good'
                        : 'Strong'}
                    </Text>
                  </Box>
                )}
                {errors.password && (
                  <Text fontSize="14px" color="error.700" mt="5px" ml="5px">
                    {errors.password.message}
                  </Text>
                )}
              </Box>
              <Box mt="20px">
                <Text fontWeight={500} fontSize="16px" color="neutrals.900" mb="10px">
                  Confirm Password
                </Text>
                <Flex alignItems="center" position="relative">
                  <Input
                    style={{
                      fontSize: '16px',
                      fontWeight: 400
                    }}
                    id="confirm-password"
                    placeholder="Confirm your password"
                    _placeholder={{
                      color: 'neutrals.300'
                    }}
                    borderColor={errors.confirmPassword ? 'error.700' : 'neutrals.300'}
                    variant="outline"
                    type={showConfirmPassword ? 'text' : 'password'}
                    p="12px 16px"
                    h="48px"
                    autoComplete="new-password"
                    borderRadius="8px"
                    {...register('confirmPassword', {
                      required: 'Please confirm your password.',
                      validate: (value) => value === password || 'The passwords do not match.'
                    })}
                  />
                  <Box
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    position="absolute"
                    right="10px"
                    bg="transparent"
                    color="neutrals.900"
                    _hover={{ bg: 'transparent', cursor: 'pointer' }}
                    _active={{ bg: 'transparent' }}
                    _focus={{ boxShadow: 'none' }}
                  >
                    {showConfirmPassword ? <EyeSlash size={24} /> : <Eye size={24} />}
                  </Box>
                </Flex>
                {errors.confirmPassword && (
                  <Text fontSize="14px" color="error.700" mt="5px" ml="5px">
                    {errors.confirmPassword.message}
                  </Text>
                )}
              </Box>
              <Box mt="20px">
                <Flex alignItems="center">
                  <Checkbox
                    variant="subtle"
                    colorPalette="gray"
                    cursor="pointer"
                    id="agree-to-terms"
                    checked={agreeToTerms}
                    {...register('agreeToTerms', {
                      required: 'You must agree to the terms and conditions.'
                    })}
                  >
                    <Text fontSize="16px" color="neutrals.500">
                      I agree to the{' '}
                      <ChakraLink
                        href="https://splashy-leopon-ea4.notion.site/terms-of-use"
                        target="_blank"
                        color="neutrals.500"
                        textDecoration="underline"
                      >
                        Terms and Conditions
                      </ChakraLink>
                    </Text>
                  </Checkbox>
                </Flex>
                {errors.agreeToTerms && (
                  <Text fontSize="14px" color="error.700" mt="5px" ml="5px">
                    {errors.agreeToTerms.message}
                  </Text>
                )}
              </Box>
              {submitError && (
                <Text fontSize="14px" color="error.700" mt="12px" ml="5px">
                  {submitError}
                </Text>
              )}
              <Box mt="20px">
                <Button
                  loading={isSubmitting}
                  bg="neutrals.1000"
                  w="100%"
                  h="60px"
                  color="neutrals.0"
                  borderRadius="8px"
                  type="submit"
                  fontSize="20px"
                  fontWeight="600"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  _hover={{ bg: 'primary.800' }}
                  disabled={!watch('agreeToTerms')}
                >
                  Next
                </Button>
              </Box>
              <Box mt="32px" textAlign="center">
                <Text fontSize="14px" color="neutrals.500">
                  Already have an account?{' '}
                  <ReactRouterLink
                    to="/login"
                    style={{
                      color: 'var(--chakra-colors-primary)',
                      textDecoration: 'none'
                    }}
                  >
                    <Text as="span" color="primary.900">
                      Log in
                    </Text>
                  </ReactRouterLink>
                </Text>
              </Box>
            </Box>
          </Fieldset.Root>
        </form>
      </Stack>
    </Stack>
  );
}
